<template>
  <v-rect v-for="[noteId, note] in notes" :key="noteId" :config="noteConfigCache.get(note)"></v-rect>
</template>

<script setup>
import { computed, inject, reactive, shallowRef, triggerRef, watch, watchEffect } from 'vue';
import { useChartRenderStore } from '../../stores/ChartRenderStore';
import { useSelectionStore } from '../../stores/SelectionStore';

const mainChart = inject('chart')
const { notes, orderedCL, notesByEventLine } = mainChart
const mainChartRender = inject('chartRender')
const { clVisibleBeats, clPixelPerBarline } = mainChartRender
const noteConfigCache = shallowRef(new WeakMap())

const previousVisibility = []

const chartRenderStore = useChartRenderStore()
const selectionStore = useSelectionStore()

watchEffect(
  async () => {
    notes.value
    orderedCL.value.forEach((cl, clIndex) => {
      const visibleBeats = clVisibleBeats.value[clIndex]
      if (!visibleBeats) {
        if (previousVisibility[clIndex]) {
          turnOffRange(cl.eventLine.eventLineId, previousVisibility[clIndex][0], previousVisibility[clIndex][1])
        }
        previousVisibility[clIndex] = null
        return
      }

      const clTime = chartRenderStore.timeToPixel(cl.eventLine.time)
      for (const b of notesByEventLine.get(cl.eventLine.eventLineId).rangeQuery(visibleBeats[0], visibleBeats[1])) {
        if (b.length <= 0) continue

        const y = b[0].beat * clPixelPerBarline.value[clIndex] + clTime
        b.forEach(e => {
          let cfg = noteConfigCache.value.get(e)

          if (!cfg) {
            cfg = reactive({
              stroke: 'black',
            })
            noteConfigCache.value.set(e, cfg)
          }

          cfg.width = chartRenderStore.columnWidth
          cfg.height = chartRenderStore.noteHeight
          cfg.fill = chartRenderStore.getSubdivisionColour(e.denominator)
          cfg.x = e.column.columnIndex * chartRenderStore.columnWidth
          cfg.y = y
          cfg.visible = true
        })
      }

      // visible off the notes that got outside visible range
      if (previousVisibility[clIndex]) {
        if (previousVisibility[clIndex][0] < visibleBeats[0]) {
          turnOffRange(cl.eventLine.eventLineId, previousVisibility[clIndex][0], visibleBeats[0])
        }
        if (previousVisibility[clIndex][1] > visibleBeats[1]) {
          turnOffRange(cl.eventLine.eventLineId, visibleBeats[1], previousVisibility[clIndex][1])
        }
      }
      previousVisibility[clIndex] = [visibleBeats[0], visibleBeats[1]]
    })
    triggerRef(noteConfigCache)
  })

const turnOffRange = (eventLineId, start, end) => {
  for (const b of notesByEventLine.get(eventLineId).rangeQuery(start, end)) {
    if (b.length <= 0) continue

    b.forEach(e => {
      let cfg = noteConfigCache.value.get(e)
      if (cfg) cfg.visible = false
    })
  }
}

let previousNotes = new Set()

watchEffect(() => {
  const n = new Set(selectionStore.selectedNotesChordMap.keys())

  const removed = previousNotes.difference(n)
  const added = n.difference(previousNotes)

  previousNotes = n

  for (const id of removed) {
    const note = notes.value.get(id)
    if (note != null) {
      const cfg = noteConfigCache.value.get(note)
      cfg.stroke = 'black'
    }
  }

  for (const id of added) {
    const note = notes.value.get(id)
    if (note != null) {
      const cfg = noteConfigCache.value.get(note)
      cfg.stroke = 'yellow'
    }
  }
})

</script>
